import {
	type BaseQueryFn,
	createApi,
	type FetchArgs,
	type FetchBaseQueryError,
	type FetchBaseQueryMeta,
	fetchBaseQuery,
	QueryStatus,
} from "@reduxjs/toolkit/query/react";
import { refreshToken as refreshTokenService } from "@sveabank/cc-common";
import { Mutex } from "async-mutex";
import { v4, validate } from "uuid";
import { setError } from "../slices/errorSlice";
import type { AppState } from "../store";
import { APPCONFIG } from "../utils/config";
import { normalizeError } from "../utils/error-handling";
import {
	getBearerToken,
	getRefreshToken,
	getRevision,
	setBearerToken,
	setRefreshToken,
	setRevision,
} from "../utils/storage";

const ONBOARDING_URL = APPCONFIG.ONBOARDING_BASE_URL;
export const BASE_PATH = "org/new";
export const CLC_BASE_PATH = "create-limited-company";
export const HEADERS = {
	"X-Svea-Clientapp": `SveaBankOnboardingWeb / ${APPCONFIG.RELEASE_NUMBER}`,
};
const mutex = new Mutex();

const getSessionId = () => {
	const sessionId = sessionStorage.getItem("sessionId");

	if (!sessionId) {
		const newSessionId = v4();
		sessionStorage.setItem("sessionId", newSessionId);
		return newSessionId;
	}
	return sessionId;
};

type ExtraOptions = {
	withAuth?: boolean;
} & (
	| { revision: true | undefined; allowRevisionConflict?: boolean }
	| { revision?: false; allowRevisionConflict?: never }
);
const baseQuery: BaseQueryFn<
	string | FetchArgs,
	unknown,
	FetchBaseQueryError,
	ExtraOptions | undefined,
	FetchBaseQueryMeta
> = (args, api, extraOptions = {}) =>
	fetchBaseQuery({
		baseUrl: ONBOARDING_URL,
		headers: HEADERS,
		prepareHeaders: (headers) => {
			const currentRevision = getRevision();
			if (extraOptions?.revision && currentRevision) {
				headers.set("If-Match", currentRevision);
			}

			if (extraOptions?.withAuth) {
				headers.set("Authorization", `Bearer ${getBearerToken()}`);
			}

			headers.set("X-Svea-SessionId", getSessionId());
			return headers;
		},
		responseHandler: async (response) => {
			const etag = response.headers.get("ETag");
			if (etag) {
				setRevision(etag);
			}
			if (response.status === 204) return await response.text();
			if (typeof args !== "string" && args.method === "HEAD") return {};

			return await response.json();
		},
	})(args, api, extraOptions);

const sveaBaseQuery: BaseQueryFn<
	string | FetchArgs,
	unknown,
	FetchBaseQueryError,
	ExtraOptions,
	FetchBaseQueryMeta
> = async (args, api, extraOptions?) => {
	// Only allow one revision mutation simultaneously per endpointName
	if (extraOptions?.revision) {
		const { mutations } = (api.getState() as AppState).onboardingApi;

		const loadingMutations = Object.keys(mutations).filter(
			(key) =>
				mutations[key]?.status === QueryStatus.pending &&
				mutations[key]?.endpointName === api.endpoint,
		);

		if (loadingMutations.length > 1) {
			api.abort();
		}
	}

	await mutex.waitForUnlock();

	const result = await baseQuery(args, api, extraOptions);

	if (result.error) {
		const errorObject = normalizeError(result.error);
		errorObject.correlationId =
			result.meta?.response?.headers.get("x-svea-correlationid") || undefined;

		if (errorObject.statusCode === 412 && extraOptions?.allowRevisionConflict) {
			const arr = result.meta?.request.url.split("/") || [];
			const applicationId = arr[arr.indexOf("application") + 1];

			if (!validate(applicationId)) throw "Invalid application id"; //"nu är det kokta fläsket stekt";

			try {
				await baseQuery(
					{
						url: `${BASE_PATH}/application/${applicationId}`,
						method: "HEAD",
					},
					api,
					{ withAuth: true },
				);

				const retryRequest = await baseQuery(args, api, extraOptions);

				if (retryRequest.error && retryRequest.error.status === 412) {
					api.dispatch(
						setError({
							statusCode: 412,
							errorCode: "InvalidRevision",
							errorMsg: "Invalid revision",
						}),
					);
				}

				return retryRequest;
			} catch {
				api.dispatch(
					setError({
						statusCode: 401,
						errorCode: "CUSTOM_ERROR",
						errorMsg: "Unknown error",
					}),
				);
			}
		}

		if (errorObject.statusCode === 401) {
			if (!mutex.isLocked()) {
				const release = await mutex.acquire();
				const refreshToken = getRefreshToken();

				if (refreshToken) {
					try {
						const refresh = await refreshTokenService(
							BASE_PATH,
							APPCONFIG.RELEASE_NUMBER,
							refreshToken,
						);

						if (refresh) {
							setBearerToken(refresh.bearerToken);
							setRefreshToken(refresh.refreshToken);
						}
					} catch {
						api.dispatch(
							setError({
								statusCode: 401,
								errorCode: "LOGGED_OUT",
							}),
						);
					} finally {
						release();
					}
				}
				const orgRequest = await baseQuery(args, api, extraOptions);
				if (orgRequest.error) {
					api.dispatch(setError({ statusCode: 401, errorMsg: "LOGGED_OUT" }));
				}
				return orgRequest;
			}

			await mutex.waitForUnlock();
			return await baseQuery(args, api, extraOptions);
		}

		// Don't open error modal on aborted requests
		if (!result.meta?.request.signal.aborted) api.dispatch(setError(errorObject));
	}

	return result;
};

export const onboardingApi = createApi({
	tagTypes: [
		"CurrentClcApplication",
		"CurrentApplication",
		"BeneficialOwners",
		"FullAccessUsers",
		"AssignedGuarantors",
		"AMLQuestions",
		"BOQuestions",
		"DetailedApplications",
		"ClcApplications",
		"ClcCards",
		"ClcCards",
		"ClcPackage",
		"ClcAmlQuestions",
		"ClcApplicationQuestions",
	],
	reducerPath: "onboardingApi",
	baseQuery: sveaBaseQuery,
	endpoints: () => ({}),
});
